import { jsx } from "react/jsx-runtime";
import { cva } from "@/styled-system/css";
import ApexForm from "../ApexForm/ApexForm.esm.js";
const DESKTOP = "@media (min-width: 950px)";
const MAX_FULL_WIDTH_FIELD_COUNT = 8;
const DEFAULT_FULL_WIDTH_FIELD_COUNT = 2;
const twoColumnRecipe = cva({
  base: {
    [DESKTOP]: {
      /*
       * `:has(button[type="submit"])` limits the grid to the data-entry
       * phase. On success ApexFields renders nothing (so ApexSubmit's
       * button is gone) and ApexSuccess's content must fall back to normal
       * block flow instead of becoming a grid item. ApexSubmit hard-codes
       * type="submit" and omits `type` from its props, and FieldTooltip
       * buttons are type="button", so this discriminates exactly.
       *
       * This is stricter than the experiment's own `#group-hero:has(button)`
       * guard, which is always true on the live page because of the hero
       * tooltip button.
       */
      '& > form:has(button[type="submit"])': {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        alignItems: "stretch",
        // Vertical rhythm already comes from each field's own
        // marginBottom: 'tighter', matching the experiment's `gap: 0 15px`.
        rowGap: "0",
        // No 15px spacing token exists (tighter=8px, tight=16px).
        columnGap: "15px",
        /*
         * Default to a full row. Catches the heading, intro and legal
         * paragraphs, dividers, and RadioField's <fieldset>. The
         * experiment left these in a single 1fr column with an empty cell
         * beside them — a latent bug we deliberately do not reproduce.
         *
         * Note CheckboxField is not caught here: v10 FieldSet only renders
         * a <fieldset> when a `legend` is passed and CheckboxField passes
         * only `description`, so it renders a <div> and is half-width like
         * any other field.
         *
         * Every direct child also spans the same three rows (label /
         * control / help) so that paired fields stay in lockstep and the
         * subgrid below has three parent rows to inherit.
         */
        "& > *": {
          gridColumn: "span 2",
          gridRow: "span 3"
        },
        /*
         * Fields are the <Box> wrappers that Text/Email/Name/Phone/Select
         * and Radio fields render, plus the button group. Half-width, so
         * they pair two-per-row in authored order.
         */
        "& > div": {
          gridColumn: "span 1",
          display: "flex",
          alignItems: "stretch"
        },
        // Lets the field fill its column in the flex fallback path below.
        // Inert once the field becomes a grid container via subgrid.
        "& > div > *": {
          flex: "1"
        },
        /*
         * Bottom-align the stack for fields with no FormField —
         * RadioField's FieldSet, and any future field whose internals are
         * not label/control/help. This lines the controls up as long as
         * the trailing help blocks are the same height, which is the best
         * that can be done without knowing the internal structure.
         *
         * Deliberately excludes FormField, which the subgrid below turns
         * into a grid container: leaving `justify-content: flex-end` on it
         * would be at best inert and at worst fight the row tracks.
         */
        '& > div > *:not([data-c11n-component="FormField"])': {
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end"
        },
        /*
         * Align label, control and help across the whole row via subgrid.
         *
         * FormField renders exactly three children — the label <Box>, the
         * control root, and <FormHelp> — so inheriting the parent's three
         * rows puts each of them on a shared track. The control track is
         * therefore the same height and offset in both columns no matter
         * how far the label or the author-supplied `help` text wraps.
         *
         * This replaces bottom-aligning the whole stack, which only lined
         * the inputs up when both help blocks happened to be the same
         * height. `help` is author text and wraps, so a field with help
         * paired against one without pushed its input out of line by
         * 46-154px depending on column width. Reachable via
         * fullWidthFieldCount, and MOPS-29102 changes the Rentals fields.
         *
         * Gated on @supports so that browsers without subgrid keep the
         * field in normal block flow at full width, rather than becoming
         * a plain auto-row grid: `display: grid` is a separate declaration
         * and would survive on its own even where `subgrid` is dropped.
         */
        "@supports (grid-template-rows: subgrid)": {
          '& > div:has(> [data-c11n-component="FormField"])': {
            display: "grid",
            gridTemplateRows: "subgrid"
          },
          '& > div > [data-c11n-component="FormField"]': {
            display: "grid",
            gridTemplateRows: "subgrid",
            gridRow: "span 3"
          }
        },
        // Tightens the help text under the first field.
        "& > div:first-of-type p": {
          marginBottom: "10px"
        },
        // The submit button row.
        "& > div:has(button)": {
          alignItems: "center"
        }
      }
    }
  },
  variants: {
    /*
     * The experiment full-widthed `div:first-of-type` and `:nth-of-type(2)` —
     * the "I manage" select (which carries three lines of help text) and
     * "Full name". That is content-driven rather than positional, and
     * MOPS-29102 is about to change the Rentals field list, so the count is
     * authored. Values are enumerated as literals for static extraction.
     *
     * Note `:nth-of-type` counts every <div> child of the form, so authors
     * should keep pre-field content as heading/paragraph blocks.
     */
    fullWidthFieldCount: {
      "0": {},
      "1": {
        [DESKTOP]: {
          '& > form:has(button[type="submit"]) > div:nth-of-type(-n+1)': {
            gridColumn: "span 2"
          }
        }
      },
      "2": {
        [DESKTOP]: {
          '& > form:has(button[type="submit"]) > div:nth-of-type(-n+2)': {
            gridColumn: "span 2"
          }
        }
      },
      "3": {
        [DESKTOP]: {
          '& > form:has(button[type="submit"]) > div:nth-of-type(-n+3)': {
            gridColumn: "span 2"
          }
        }
      },
      "4": {
        [DESKTOP]: {
          '& > form:has(button[type="submit"]) > div:nth-of-type(-n+4)': {
            gridColumn: "span 2"
          }
        }
      },
      "5": {
        [DESKTOP]: {
          '& > form:has(button[type="submit"]) > div:nth-of-type(-n+5)': {
            gridColumn: "span 2"
          }
        }
      },
      "6": {
        [DESKTOP]: {
          '& > form:has(button[type="submit"]) > div:nth-of-type(-n+6)': {
            gridColumn: "span 2"
          }
        }
      },
      "7": {
        [DESKTOP]: {
          '& > form:has(button[type="submit"]) > div:nth-of-type(-n+7)': {
            gridColumn: "span 2"
          }
        }
      },
      "8": {
        [DESKTOP]: {
          '& > form:has(button[type="submit"]) > div:nth-of-type(-n+8)': {
            gridColumn: "span 2"
          }
        }
      }
    }
  }
});
const clampFullWidthFieldCount = (value) => {
  const fallback = String(DEFAULT_FULL_WIDTH_FIELD_COUNT);
  if (value === void 0 || value === null || value === "") {
    return fallback;
  }
  const parsed = Math.round(Number(value));
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  if (parsed < 0) {
    return "0";
  }
  if (parsed > MAX_FULL_WIDTH_FIELD_COUNT) {
    return String(MAX_FULL_WIDTH_FIELD_COUNT);
  }
  return String(parsed);
};
const ApexFormTwoColumn = ({
  fullWidthFieldCount,
  children,
  ...apexFormProps
}) => /* @__PURE__ */ jsx("div", { className: twoColumnRecipe({
  fullWidthFieldCount: clampFullWidthFieldCount(fullWidthFieldCount)
}), children: /* @__PURE__ */ jsx(ApexForm, { ...apexFormProps, children }) });
export {
  DEFAULT_FULL_WIDTH_FIELD_COUNT,
  MAX_FULL_WIDTH_FIELD_COUNT,
  clampFullWidthFieldCount,
  ApexFormTwoColumn as default
};
